import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import mondaySdk from "monday-sdk-js";
import { SettingSwitch } from "../../SettingSwitch";
import VisualIndicatorPicker from "./VisualIndicatorPicker";
import VisualIndicatorBanner from "./VisualIndicatorBanner";
import { ApplyChoice, VisualIndicatorSaveDialog, VisualIndicatorTurnOffDialog } from "./VisualIndicatorDialogs";
import {
  INVALID_REASON_COPY,
  VISUAL_INDICATOR_EXISTING_ITEMS,
  VisualIndicatorInvalidReason,
  displayToken,
  toToken,
} from "./visualIndicator";
import useVisualIndicator from "../../../../../hooks/api/settings/useVisualIndicator";
import useVisualIndicatorJob, { VisualIndicatorJobInfo } from "../../../../../hooks/api/settings/useVisualIndicatorJob";
import logger from "../../../../../lib/logger";
import type {
  VisualIndicatorAccountSettings,
  VisualIndicatorMode,
  VisualIndicatorSetting as IndicatorSetting,
} from "../../types/settings-drawer-interface";

const monday = mondaySdk();

export const VISUAL_INDICATOR_TOOLTIP = "Adds your chosen emoji or label to the start of every synced item's name";
export const VISUAL_INDICATOR_OFF_HINT =
  "Turn on to mark synced items with an emoji or a short label, so your team can spot them on any board.";

const notice = (message: string, type: "success" | "error" | "info" = "success") =>
  monday.execute("notice", { message, type, timeout: 5000 });

/** The backend answers 501 on every visual indicator route while app-ms is disabled. */
const isUnavailable = (err: any): boolean => err?.response?.status === 501;

const errorCopy = (err: any, fallback: string): string => {
  const status = err?.response?.status;
  if (status === 501) return "Custom indicators aren't available yet.";
  const reason = err?.response?.data?.reason as VisualIndicatorInvalidReason | undefined;
  if (status === 400 && reason && INVALID_REASON_COPY[reason]) return INVALID_REASON_COPY[reason];
  return fallback;
};

interface VisualIndicatorSettingProps {
  settings: (VisualIndicatorAccountSettings & Record<string, any>) | null;
  /** The switch position (AccountSettings' switchValues). */
  enabled?: boolean;
  setSwitchValues: Dispatch<SetStateAction<any>>;
  updateAccountSetting: (settingKey: string, value: boolean) => Promise<void>;
  /** Re-read account settings after a save. */
  onSettingsChanged?: () => void;
}

/**
 * Settings › Account › Preferences › Visual indicator: the switch, the picker under it, the Save
 * and switch-off dialogs, and the banner for a bulk rename (spec "UI (frontend)", mockup frames 0–5).
 */
const VisualIndicatorSetting = ({
  settings,
  enabled,
  setSwitchValues,
  updateAccountSetting,
  onSettingsChanged,
}: VisualIndicatorSettingProps) => {
  const vi = useVisualIndicator();
  // The indicator as last saved from this page, until settings are re-read.
  const [savedOverride, setSavedOverride] = useState<IndicatorSetting | null | undefined>(undefined);
  const [pendingSave, setPendingSave] = useState<{ indicator: IndicatorSetting; estimatedItems: number } | null>(null);
  const [offDialog, setOffDialog] = useState<{ estimatedItems: number } | null>(null);
  const [busy, setBusy] = useState(false);
  // While the backend answers 501 the switch works as it always has, with no picker or dialogs.
  const [unavailable, setUnavailable] = useState(false);
  const noteUnavailable = (err: any) => {
    if (isUnavailable(err)) setUnavailable(true);
  };

  // One cheap probe per mount, so a 501 hides the picker before anyone uses it.
  const { ready, estimate } = vi;
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    estimate().catch((err: any) => {
      if (!cancelled && isUnavailable(err)) setUnavailable(true);
    });
    return () => {
      cancelled = true;
    };
  }, [ready, estimate]);

  const settingsIndicator = settings?.visual_indicator ?? null;
  useEffect(() => setSavedOverride(undefined), [settingsIndicator]);

  const saved = savedOverride !== undefined ? savedOverride : settingsIndicator;
  const current = displayToken(toToken(saved));

  const infoForResumed = useCallback((): VisualIndicatorJobInfo => {
    if (!settings?.visual_indicator_setting) return { mode: "remove", from: current };
    const previous = [...(settings?.visual_indicator_history ?? [])].reverse().find((t) => displayToken(t) !== current);
    return { mode: "swap", to: current, from: previous ? displayToken(previous) : undefined };
  }, [settings, current]);

  const { job, track, stop, dismiss } = useVisualIndicatorJob({
    initialRequestId: settings?.visual_indicator_job?.requestId ?? null,
    infoForResumed,
  });

  const commit = async (mode: VisualIndicatorMode, indicator: IndicatorSetting, estimatedItems = 0) => {
    const from = current;
    const to = displayToken(toToken(indicator));
    setBusy(true);
    try {
      const result = await vi.save(mode, indicator);
      setSavedOverride(indicator);
      onSettingsChanged?.();
      setPendingSave(null);
      if (mode === "swap" && result?.requestId) {
        track(result.requestId, { mode: "swap", from, to }, result.estimatedItems ?? estimatedItems);
      } else {
        notice(
          estimatedItems > 0 || VISUAL_INDICATOR_EXISTING_ITEMS === "never"
            ? `Saved. New syncs will show ${to}; existing items keep their indicator.`
            : `Saved. New syncs will show ${to}.`,
        );
      }
    } catch (err: any) {
      logger.error("Error saving visual indicator:", err);
      noteUnavailable(err);
      notice(errorCopy(err, "Failed to save the visual indicator"), "error");
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async (indicator: IndicatorSetting) => {
    if (VISUAL_INDICATOR_EXISTING_ITEMS === "never") return commit("save", indicator);
    let estimatedItems: number;
    try {
      estimatedItems = await vi.estimate();
    } catch (err: any) {
      logger.error("Error estimating visual indicator items:", err);
      noteUnavailable(err);
      notice(errorCopy(err, "Failed to save the visual indicator"), "error");
      return;
    }
    if (estimatedItems > 0) setPendingSave({ indicator, estimatedItems });
    else await commit("save", indicator);
  };

  const confirmSave = (choice: ApplyChoice) => {
    if (!pendingSave) return;
    commit(choice === "all" ? "swap" : "save", pendingSave.indicator, pendingSave.estimatedItems);
  };

  // Switching off asks first when items already show the indicator; switching on saves as before.
  const interceptChange = async (checked: boolean) => {
    if (checked || unavailable) return true;
    try {
      const estimatedItems = await vi.estimate();
      if (estimatedItems > 0) {
        setOffDialog({ estimatedItems });
        return false;
      }
    } catch (err: any) {
      // Without a count there is nothing to offer; switch off as before.
      logger.error("Error estimating visual indicator items:", err);
      noteUnavailable(err);
    }
    return true;
  };

  const confirmTurnOff = async (alsoRemove: boolean) => {
    if (!offDialog) return;
    const { estimatedItems } = offDialog;
    setBusy(true);
    try {
      await updateAccountSetting("visual_indicator_setting", false);
      setSwitchValues((prev: any) => ({ ...prev, visual_indicator_setting: false }));
    } catch (err: any) {
      // updateAccountSetting has already told the user.
      setBusy(false);
      return;
    }
    setOffDialog(null);
    if (alsoRemove) {
      try {
        const result = await vi.save("remove");
        if (result?.requestId) track(result.requestId, { mode: "remove", from: current }, result.estimatedItems ?? estimatedItems);
      } catch (err: any) {
        logger.error("Error removing the visual indicator:", err);
        notice(errorCopy(err, `Failed to start removing ${current} from existing items`), "error");
      }
    }
    setBusy(false);
  };

  const stopJob = async () => {
    try {
      await stop();
    } catch (err: any) {
      logger.error("Error stopping the visual indicator job:", err);
      notice("Failed to stop updating item names", "error");
    }
  };

  return (
    <>
      {job && <VisualIndicatorBanner job={job} onStop={stopJob} onDismiss={dismiss} />}
      <SettingSwitch
        label="Visual indicator"
        settingKey="visual_indicator_setting"
        tooltip={VISUAL_INDICATOR_TOOLTIP}
        initialValue={enabled}
        setNewValue={setSwitchValues}
        updateFunction={(value) => updateAccountSetting("visual_indicator_setting", value)}
        interceptChange={interceptChange}
      />
      {settings && !enabled && !unavailable && (
        <p className="text-light-secondary-text-color" style={{ margin: "-4px 0 0", fontSize: 13, color: "#676879" }}>
          {VISUAL_INDICATOR_OFF_HINT}
        </p>
      )}
      {enabled && !unavailable && <VisualIndicatorPicker saved={saved} onSave={handleSave} saving={busy || vi.saving} />}

      {pendingSave && VISUAL_INDICATOR_EXISTING_ITEMS !== "never" && (
        <VisualIndicatorSaveDialog
          from={current}
          to={displayToken(toToken(pendingSave.indicator))}
          estimatedItems={pendingSave.estimatedItems}
          policy={VISUAL_INDICATOR_EXISTING_ITEMS}
          saving={busy}
          onConfirm={confirmSave}
          onCancel={() => setPendingSave(null)}
        />
      )}
      {offDialog && (
        <VisualIndicatorTurnOffDialog
          current={current}
          estimatedItems={offDialog.estimatedItems}
          saving={busy}
          onConfirm={confirmTurnOff}
          onCancel={() => setOffDialog(null)}
        />
      )}
    </>
  );
};

export default VisualIndicatorSetting;
